import axios, { Method, AxiosResponse } from "axios";

import config from "../config";
import { warn } from "./messages";
import store from "./redux/store";
import { getUser } from "../keycloak";
import { userManager } from "../index";
import { signOut } from "./userSession";
import setting from "../../package.json";
import { intl } from "../containers/app";
import { setShouldReload } from "./redux/reload/actions";

store.subscribe(listener);

/**
 * třída pro uložení aktuálního ID klienta
 */
class HttpData {
  /**
   * zvoleny jazyk
   */
  static language: string;
}

/**
 * při změně redux store nastaví aktuálního klienta
 */
function listener() {
  const language = store.getState().language;
  HttpData.language = language.data && language.data.localization;
}

/**
 * třída pro jednorázové použití tokenu bez přihlášení
 */
export class Token {
  /**
   * token
   */
  static value: string;
}

/**
 * přednastavená instance axios
 */
const instance = axios.create({
  baseURL: config.BASE_URL,
  timeout: config.TIMEOUT,
  validateStatus: (status) => status < 500 && status !== 403,
});

instance.interceptors.response.use(
  (response) => {
    response &&
      response.headers &&
      response.headers["app-update"] &&
      store.dispatch(setShouldReload(true));
    return response;
  },
  (error) => {
    if (error && error.response) {
      if (error.response.status === 401) {
        signOut(userManager, true);
      } else if (error.response.status === 403) {
        warn(
          intl.formatMessage({
            id: "session.authorizationError",
            defaultMessage: "K této operaci nemáte práva!",
          })
        );
      }
    }
    return error;
  }
);

/**
 * zkontroluje HTTP status, jestli je 200 až 304
 * @param {number} response HTTP status
 */
export const checkHttpStatus = (response: AxiosResponse) =>
  response ? response.status >= 200 && response.status <= 304 : false;

/**
 * zašle HTTP PUT požadavek
 * @param {string} endpoint endpoint cílového serveru
 * @param {any} data data k odeslaní
 * @param {boolean} auth jestli se ma použit token
 * @param {boolean} json false --> nechá defaultní formát dat - neprovede serializaci na JSON
 */
export async function put(
  endpoint: string,
  data: object,
  auth: boolean = true,
  json: boolean = true
) {
  return await sendRequest("put", endpoint, data, auth, json);
}

/**
 * zašle HTTP POST požadavek
 * @param {string} endpoint endpoint cílového serveru
 * @param {any} data data k odeslaní
 * @param {boolean} auth jestli se ma použit token
 * @param {boolean} json false --> nechá defaultní formát dat - neprovede serializaci na JSON
 * @param {number} timeout timeout pozadavku [ms]
 */
export async function post(
  endpoint: string,
  data: any,
  auth: boolean = true,
  json: boolean = true,
  timeout: number = config.TIMEOUT
) {
  return await sendRequest(
    "post",
    endpoint,
    data,
    auth,
    json,
    undefined,
    false,
    timeout
  );
}

/**
 * zašle HTTP PATCH požadavek
 * @param {string} endpoint endpoint cílového serveru
 * @param {any} data data k odeslaní
 * @param {boolean} auth jestli se ma použit token
 */
export async function patch(
  endpoint: string,
  data: object,
  auth: boolean = true
) {
  return await sendRequest("patch", endpoint, data, auth);
}

/**
 * zašle HTTP POST požadavek, ale nechá defaultní formát dat - neprovede serializaci na JSON
 * @param {string} endpoint endpoint cílového serveru
 * @param {any} data data k odeslaní
 * @param {boolean} auth jestli se ma použit token
 * @param {any} onUploadProgress metoda, která vrací informace o průběhu uploadu dat
 * @param {number} timeout timeout pozadavku [ms]
 */
export async function postForm(
  endpoint: string,
  data: object,
  auth: boolean = true,
  onUploadProgress: any = undefined,
  timeout: number = config.TIMEOUT
) {
  return await sendRequest(
    "post",
    endpoint,
    data,
    auth,
    false,
    onUploadProgress,
    false,
    timeout
  );
}

/**
 * zašle HTTP GET požadavek
 * @param {string} endpoint endpoint cílového serveru
 * @param {boolean} auth jestli se ma použit token
 * @param {boolean} isBlob jestli je vysledek pozadavku blob
 */
export async function get(
  endpoint: string,
  auth: boolean = true,
  isBlob: boolean = false
) {
  return await sendRequest(
    "get",
    endpoint,
    undefined,
    auth,
    true,
    undefined,
    isBlob,
    config.TIMEOUT
  );
}

/**
 * zašle HTTP DELETE požadavek
 * @param {string} endpoint endpoint cílového serveru
 * @param {boolean} auth jestli se ma použit token
 */
export async function deleteApi(endpoint: string, auth: boolean = true) {
  return await sendRequest("delete", endpoint, undefined, auth);
}

/**
 * zašle HTTP požadavek
 * @param {Method} method http metoda
 * @param {string} endpoint endpoint cílového serveru
 * @param {any} data data k odeslaní
 * @param {boolean} auth použití autentizace
 * @param {boolean} json serializace dat do JSON
 * @param {any} onUploadProgress metoda, která vrací informace o průběhu uploadu dat
 * @param {boolean} isBlob jestli je vysledek pozadavku blob
 * @param {number} timeout timeout pozadavku [ms]
 */
async function sendRequest(
  method: Method,
  endpoint: string,
  data: any,
  auth: boolean,
  json: boolean = true,
  onUploadProgress: any = undefined,
  isBlob: boolean = false,
  timeout: number = config.TIMEOUT
) {
  return await instance.request({
    method,
    timeout,
    url: endpoint,
    onUploadProgress,
    data: json ? JSON.stringify(data) : data,
    responseType: isBlob ? "blob" : undefined,
    headers: auth ? await getBearerHeaders() : getHeaders(),
  });
}

/**
 * vytvoří objekt hlavičky
 * @param {string} token načtený JWT
 */
const getBearerHeaders = async (token?: string) => {
  if (token === undefined) {
    try {
      token = getUser()?.access_token;
    } catch (err) {
      if (Token.value) {
        token = Token.value;
      } else {
        return undefined;
      }
    }
  }
  return {
    ...getHeaders(),
    Authorization: "Bearer " + token,
  };
};

/**
 * vytvoří základní hlavičky
 */
const getHeaders = () => ({
  Accept: "application/json",
  "App-Version": setting.version,
  "Content-Type": "application/json",
  "Accept-Language": HttpData.language,
});
